import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../utils/cn";
import { motion, AnimatePresence } from "framer-motion";

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  delay?: number;
  className?: string;
  contentClassName?: string;
  position?: "top" | "bottom" | "left" | "right";
}

const FleetrifyTooltip: React.FC<TooltipProps> = ({
  children,
  content,
  delay = 150,
  className,
  contentClassName,
  position = "top",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const positionMap = {
    top: {
      wrapper: "bottom-full mb-2 left-1/2 -translate-x-1/2",
      motion: {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10 },
      },
    },
    bottom: {
      wrapper: "top-full mt-2 left-1/2 -translate-x-1/2",
      motion: {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 10 },
      },
    },
    left: {
      wrapper: "right-full mr-2 top-1/2 -translate-y-1/2",
      motion: {
        initial: { opacity: 0, x: -10 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -10 },
      },
    },
    right: {
      wrapper: "left-full ml-2 top-1/2 -translate-y-1/2",
      motion: {
        initial: { opacity: 0, x: 10 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: 10 },
      },
    },
  };

  const { wrapper, motion: animation } = positionMap[position];

  return (
    <div
      className={cn("relative inline-block", className)}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={animation.initial}
            animate={animation.animate}
            exit={animation.exit}
            transition={{ duration: 0.2 }}
            className={cn(
              "absolute z-50 bg-gray-900 text-white text-xs rounded-md px-3 py-1 shadow-lg whitespace-nowrap",
              wrapper,
              contentClassName
            )}
            style={{ pointerEvents: "none" }}
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FleetrifyTooltip;
